// ARCHIVO COMPARTIDO app/web — editar en ambos repos. Verificar con `npm run check:shared` (TemControlApp).
//
// ── Estado de un día en el calendario ─────────────────────────────────────
// Fuente ÚNICA de la decisión "qué es este día" para un trabajador. La usan
// el calendario personal (web CalendarioPage / app CalendarioScreen) y la
// comparación de calendarios (web CompararCalendariosPage). Antes cada
// pantalla tenía sus propias reglas (la comparación marcaba cualquier
// CLOCK_IN como trabajado aunque estuviera pendiente, ignoraba los periodos
// pendientes y tenía otra prioridad; la app decidía con su propia cadena de
// `if`), así que el mismo día se veía distinto según la pantalla.
//
// Cada pantalla mantiene SU mapeo visual (colores / clases / estilos RN),
// pero la decisión del estado sale siempre de `resolveDayStatus`.
//
// Aquí viven también los helpers de estado de aprobación y el cómputo de
// días aprobados/pendientes por periodo, para que TODOS los contadores
// (calendario, ficha del trabajador) traten igual los RECHAZADOS: no cuentan
// ni como aprobados ni como pendientes.

//
// ZONA HORARIA: el día al que pertenece cada evento (y cada extremo de un
// periodo) se decide en la zona del TRABAJADOR (`timeZone`, ver
// src/lib/workerTime.js → timeZoneForWorker), NUNCA en la del dispositivo.
// Por defecto Europe/Madrid. Antes un móvil en GMT pintaba cada vacación
// creada a las 00:00 de Madrid (22:00 UTC del día anterior) un día antes.

import { EVENT_TYPES, APPROVAL_STATUS } from '../services/api';
import { countPeriods } from './vacationCounter';
import { DEFAULT_TIME_ZONE, dayKeyInZone, dayKeysBetween } from './workerTime';
import { hasToken, appendToken, removeToken } from './geoInfo';

// ── Estado de aprobación ──────────────────────────────────────────────────
// Códigos del backend: 0 = PENDING, 1 = APPROVED, 2 = REJECTED. Se normaliza
// con Number() por si algún endpoint lo devuelve como string.
//   · Aprobado  → exactamente APPROVED.
//   · Rechazado → exactamente REJECTED.
//   · Pendiente → ni aprobado ni rechazado (incluye null/undefined).
export const isApprovedStatus = (s) => s != null && Number(s) === APPROVAL_STATUS.APPROVED;
export const isRejectedStatus = (s) => s != null && Number(s) === APPROVAL_STATUS.REJECTED;
export const isPendingStatus = (s) => !isApprovedStatus(s) && !isRejectedStatus(s);

// Empareja START/END en orden cronológico. Si llegan dos START seguidos, el
// anterior se descarta (solo cuenta el ÚLTIMO START sin emparejar). Un START
// huérfano al final se trata como periodo de un solo día (`endEvent: null`,
// `open: true`). El approvalStatus del periodo es el del START.
export const pairEventsIntoPeriods = (events, startType, endType) => {
  const sorted = (events || [])
    .filter((e) => e.eventType === startType || e.eventType === endType)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const periods = [];
  let pendingStart = null;
  for (const e of sorted) {
    if (e.eventType === startType) {
      pendingStart = e;
    } else if (e.eventType === endType && pendingStart) {
      periods.push({
        start: pendingStart.timestamp, end: e.timestamp,
        startId: pendingStart.id, endId: e.id,
        approvalStatus: pendingStart.approvalStatus,
        startEvent: pendingStart, endEvent: e,
      });
      pendingStart = null;
    }
  }
  if (pendingStart) periods.push({
    start: pendingStart.timestamp, end: pendingStart.timestamp,
    startId: pendingStart.id, approvalStatus: pendingStart.approvalStatus,
    startEvent: pendingStart, endEvent: null, open: true,
  });
  return periods;
};

const buildPeriodSet = (periods, approvalMatch, timeZone) => {
  const s = new Set();
  for (const p of periods) {
    if (!approvalMatch(p.approvalStatus)) continue;
    for (const d of dayKeysBetween(dayKeyInZone(p.start, timeZone), dayKeyInZone(p.end, timeZone))) s.add(d);
  }
  return s;
};

// Construye, a partir de los eventos de UN trabajador, todos los sets de
// días que necesita `resolveDayStatus` (más los periodos emparejados, que el
// calendario personal reutiliza para contadores y edición).
// `timeZone` = zona del trabajador (timeZoneForWorker); por defecto Madrid.
export const buildDayStatusSets = (events, timeZone = DEFAULT_TIME_ZONE) => {
  const dayOf = (ts) => dayKeyInZone(ts, timeZone);
  const evs = Array.isArray(events) ? events : [];
  const vacationPeriods = pairEventsIntoPeriods(evs, EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END);
  const sickPeriods = pairEventsIntoPeriods(evs, EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END);
  const absencePeriods = pairEventsIntoPeriods(evs, EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END);

  // Fichajes: approvalStatus null/undefined cuenta como APROBADO (los
  // fichajes "en directo" creados sin el campo quedan así).
  const worked = new Set();
  const workedPending = new Set();
  // Compensación (eventType 8 ó 9): se marca el día de cada evento.
  const compensation = new Set();
  for (const e of evs) {
    const iso = dayOf(e.timestamp);
    if (!iso) continue;
    if (e.eventType === EVENT_TYPES.CLOCK_IN) {
      const status = Number(e.approvalStatus ?? APPROVAL_STATUS.APPROVED);
      if (status === APPROVAL_STATUS.APPROVED) worked.add(iso);
      else if (status === APPROVAL_STATUS.PENDING) workedPending.add(iso);
    } else if (e.eventType === EVENT_TYPES.COMPENSATION_START || e.eventType === EVENT_TYPES.COMPENSATION_END) {
      compensation.add(iso);
    }
  }

  return {
    vacationPeriods,
    sickPeriods,
    absencePeriods,
    vacationApproved: buildPeriodSet(vacationPeriods, isApprovedStatus, timeZone),
    vacationPending: buildPeriodSet(vacationPeriods, isPendingStatus, timeZone),
    sickApproved: buildPeriodSet(sickPeriods, isApprovedStatus, timeZone),
    sickPending: buildPeriodSet(sickPeriods, isPendingStatus, timeZone),
    absenceApproved: buildPeriodSet(absencePeriods, isApprovedStatus, timeZone),
    absencePending: buildPeriodSet(absencePeriods, isPendingStatus, timeZone),
    compensation,
    worked,
    workedPending,
  };
};

export const DAY_STATUS = {
  VACATION_APPROVED: 'vacationApproved',
  VACATION_PENDING: 'vacationPending',
  SICK_APPROVED: 'sickApproved',
  SICK_PENDING: 'sickPending',
  ABSENCE_APPROVED: 'absenceApproved',
  ABSENCE_PENDING: 'absencePending',
  COMPENSATION: 'compensation',
  HOLIDAY: 'holiday',
  WORKED: 'worked',
  WORKED_PENDING: 'workedPending',
  REST: 'rest',
};

export const DAY_STATUS_LABELS = {
  vacationApproved: 'Vacaciones',
  vacationPending: 'Vacaciones (pendiente)',
  sickApproved: 'Día completo',
  sickPending: 'Día completo (pendiente)',
  absenceApproved: 'Por horas',
  absencePending: 'Por horas (pendiente)',
  compensation: 'Compensación',
  holiday: 'Festivo',
  worked: 'Trabajado',
  workedPending: 'Trabajado (pendiente)',
  rest: 'Descanso',
};

// Función PURA: dado un día y los sets del trabajador, devuelve la clave de
// estado (DAY_STATUS) o null si es un laborable sin nada. Prioridad:
//   vac aprobada > vac pendiente > día completo aprobado > día completo
//   pendiente > por horas aprobada > por horas pendiente > compensación >
//   festivo > trabajado > trabajado pendiente > descanso.
// ("Hoy" NO entra aquí: cada calendario lo resalta por encima de todo en su
// propio mapeo visual.)
// `holidaysSet` es un Set<ISO> de festivos del trabajador; `isRestDay` indica
// si ese día no tiene jornada configurada.
export const resolveDayStatus = (iso, sets, { holidaysSet = null, isRestDay = false } = {}) => {
  if (!iso || !sets) return isRestDay ? DAY_STATUS.REST : null;
  if (sets.vacationApproved?.has(iso)) return DAY_STATUS.VACATION_APPROVED;
  if (sets.vacationPending?.has(iso)) return DAY_STATUS.VACATION_PENDING;
  if (sets.sickApproved?.has(iso)) return DAY_STATUS.SICK_APPROVED;
  if (sets.sickPending?.has(iso)) return DAY_STATUS.SICK_PENDING;
  if (sets.absenceApproved?.has(iso)) return DAY_STATUS.ABSENCE_APPROVED;
  if (sets.absencePending?.has(iso)) return DAY_STATUS.ABSENCE_PENDING;
  if (sets.compensation?.has(iso)) return DAY_STATUS.COMPENSATION;
  if (holidaysSet && typeof holidaysSet.has === 'function' && holidaysSet.has(iso)) return DAY_STATUS.HOLIDAY;
  if (sets.worked?.has(iso)) return DAY_STATUS.WORKED;
  if (sets.workedPending?.has(iso)) return DAY_STATUS.WORKED_PENDING;
  if (isRestDay) return DAY_STATUS.REST;
  return null;
};

// ── Regla de cómputo de días por tipo de periodo ─────────────────────────
// Vacaciones → según el `holidayType` del convenio del trabajador
//   ('laborables' por defecto, 'festivos' = naturales).
// Día completo (antes "baja") → SIEMPRE naturales (regla INSS), igual que
//   la ficha del trabajador en web y app.
// Ver src/lib/vacationCounter.js para el detalle de cada modalidad.
export const vacationHolidayTypeFor = (agreement) =>
  (agreement?.holidayType === 'festivos' ? 'festivos' : 'laborables');
export const SICK_LEAVE_HOLIDAY_TYPE = 'festivos';

// Cuenta los días de una lista de periodos separados por estado. Los
// RECHAZADOS se descartan (ni aprobados ni pendientes). `opts` son las
// opciones de countPeriods (holidayType, workHoursByDow, holidaysSet,
// timeZone y, para contadores por año, `from` / `to`).
// Devuelve { approved: { days, count, periods, scheduleUnknown },
//            pending: { … }, scheduleUnknown }.
// Con `scheduleUnknown: true` las cifras no son fiables (falta el horario):
// la UI debe mostrar "—" / "cargando".
export const countPeriodsByStatus = (periods, opts = {}) => {
  const list = Array.isArray(periods) ? periods : [];
  const approvedPeriods = list.filter((p) => isApprovedStatus(p.approvalStatus));
  const pendingPeriods = list.filter((p) => isPendingStatus(p.approvalStatus));
  const approved = countPeriods(approvedPeriods, opts);
  const pending = countPeriods(pendingPeriods, opts);
  return {
    approved: { ...approved, periods: approvedPeriods },
    pending: { ...pending, periods: pendingPeriods },
    scheduleUnknown: !!(approved.scheduleUnknown || pending.scheduleUnknown),
  };
};

// ¿El periodo (sus días solicitados, en la zona del trabajador) toca el rango
// [from, to]? Para listar / contar periodos de un año cuando los eventos se
// cargan con margen (yearCountWindow).
export const periodOverlapsRange = (p, from, to, timeZone = DEFAULT_TIME_ZONE) => {
  const s = p?.start ? dayKeyInZone(p.start, timeZone) : null;
  const e = p?.end ? dayKeyInZone(p.end, timeZone) : s;
  if (!s || !e) return false;
  return (!from || e >= from) && (!to || s <= to);
};

// ── Control de cupo de vacaciones ─────────────────────────────────────────
// Antes (AprobacionesPage) solo se comparaban los días YA aprobados con el
// cupo, sin sumar la solicitud que se iba a aprobar, emparejando distinto
// (LIFO), sin festivos y con el horario actual. Ahora se usa el MISMO
// cómputo que los contadores (countPeriods) y se mira cómo quedaría el año
// DESPUÉS de aprobar/asignar:
//   approved       → días ya aprobados en [from, to] (sin la solicitud).
//   request        → días NUEVOS que añade la solicitud (los que no estaban ya).
//   pending        → días que añadirían las OTRAS solicitudes pendientes.
//   afterApproval  → approved + request.
//   over / overBy  → afterApproval > allowance (y en cuánto).
//   overWithPending / overByWithPending → contando también las pendientes.
//   scheduleUnknown → sin horario, la cifra no es fiable.
// `periods` = periodos del trabajador (pairEventsIntoPeriods, con margen de
// año), `request` = { start, end, startId?, endId? } (se excluye de
// `periods` por id si ya estaba). `opts` = opciones de countPeriods.
export const checkVacationCap = ({ periods, request = null, allowance, ...opts } = {}) => {
  const list = Array.isArray(periods) ? periods : [];
  const isRequest = (p) => !!request && (
    (request.startId != null && p.startId === request.startId)
    || (request.endId != null && p.endId != null && p.endId === request.endId)
  );
  const others = list.filter((p) => !isRequest(p));
  const approvedP = others.filter((p) => isApprovedStatus(p.approvalStatus));
  const pendingP = others.filter((p) => isPendingStatus(p.approvalStatus));
  const withReqP = request ? [...approvedP, request] : approvedP;
  const approved = countPeriods(approvedP, opts);
  const withReq = countPeriods(withReqP, opts);
  const withAll = countPeriods([...withReqP, ...pendingP], opts);
  const cap = Number(allowance) || 0;
  return {
    allowance: cap,
    approved: approved.count,
    request: withReq.count - approved.count,
    pending: withAll.count - withReq.count,
    afterApproval: withReq.count,
    afterAll: withAll.count,
    over: cap > 0 && withReq.count > cap,
    overBy: Math.max(0, withReq.count - cap),
    overWithPending: cap > 0 && withAll.count > cap,
    overByWithPending: Math.max(0, withAll.count - cap),
    scheduleUnknown: !!(approved.scheduleUnknown || withReq.scheduleUnknown || withAll.scheduleUnknown),
  };
};

const daysLabel = (n) => `${n} día${n === 1 ? '' : 's'}`;

// Mensaje del aviso de cupo (null si no se pasa). `verb` = 'aprobar' |
// 'asignar'. `year` opcional para el texto.
//   "Tras aprobar tendría 38 de 30 días: se pasa en 8."
export const vacationCapMessage = (check, { verb = 'aprobar', year = null } = {}) => {
  if (!check || (!check.over && !check.overWithPending)) return null;
  const inYear = year ? ` en ${year}` : '';
  const detail = `(${daysLabel(check.approved)} aprobados + ${daysLabel(check.request)} de esta solicitud)`;
  if (check.over) {
    const pendingNote = check.pending > 0
      ? ` Además hay ${daysLabel(check.pending)} en otras solicitudes pendientes (con ellas serían ${check.afterAll}).`
      : '';
    return `Tras ${verb} tendría ${check.afterApproval} de ${check.allowance} días${inYear}: se pasa en ${check.overBy}. ${detail}.${pendingNote}`;
  }
  return `Tras ${verb} tendría ${check.afterApproval} de ${check.allowance} días${inYear} ${detail}, `
    + `pero con las otras solicitudes pendientes (${daysLabel(check.pending)}) llegaría a ${check.afterAll}: se pasaría en ${check.overByWithPending}.`;
};

// ── Solicitudes de eliminación de periodos ────────────────────────────────
// El trabajador pide borrar un periodo (vacaciones, día completo, por horas,
// compensación) marcando sus DOS extremos con `;reqdelete=1` en geoInfo y
// dejándolos PENDIENTES. Al revisar:
//   · Aprobar  → se borran los DOS eventos del periodo.
//   · Rechazar → se quita el flag y el periodo sigue como estaba (aprobado).
// Antes solo las vacaciones se trataban así: aprobar la eliminación de una
// ausencia la APROBABA (el periodo seguía vivo, con el flag).
export const PERIOD_EVENT_PAIRS = [
  [EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END],
  [EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END],
  [EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END],
  [EVENT_TYPES.COMPENSATION_START, EVENT_TYPES.COMPENSATION_END],
];

// [startType, endType] del periodo al que pertenece `eventType` (o null).
export const periodPairFor = (eventType) =>
  PERIOD_EVENT_PAIRS.find(([s, e]) => eventType === s || eventType === e) || null;

export const DELETE_REQUEST_FLAG = ';reqdelete=1';

// Los tokens de geoInfo se leen/escriben con src/lib/geoInfo.js (formato
// único): conviven con la ubicación (`lat,lng;acc=…;origen=…`), apprby, etc.
export const isDeleteRequestEvent = (e) =>
  !!e && !!periodPairFor(e.eventType) && hasToken(e.geoInfo, 'reqdelete', '1');

// Marca un extremo del periodo como "solicitud de eliminación". Sustituye
// cualquier `reqdelete` previo (también un `;reqdelete=0`).
export const markDeleteRequest = (geoInfo) => appendToken(geoInfo, 'reqdelete', '1');

// Quita el flag para enviarlo al backend. OJO: el PUT /api/event/:id hace
// `pikachu.geoInfo = geoInfo || pikachu.geoInfo`, así que tanto null como ''
// CONSERVAN el geoInfo anterior (con su `;reqdelete=1`). Si al quitar el flag
// no queda nada, se envía la marca explícita `;reqdelete=0` (truthy, no es
// una solicitud — isDeleteRequestEvent busca `reqdelete=1` — y el siguiente
// strip la elimina). Nunca devuelve null.
export const stripDeleteRequestFlag = (geoInfo) => {
  const raw = String(geoInfo || '');
  const cleaned = removeToken(raw, 'reqdelete');
  return cleaned || (raw ? ';reqdelete=0' : '');
};

// ── Solicitudes de eliminación de FICHAJES sueltos (entrada / salida) ─────
// Mismo token `;reqdelete=1`, pero sobre UN solo evento y sin tocar su
// `approvalStatus`: el fichaje sigue APROBADO mientras el responsable no
// decida, así que sigue contando en la jornada y en la bolsa exactamente
// igual que antes de pedir la eliminación. El registro de jornada es un
// documento legal (art. 34.9 ET): no se descuenta nada por el simple hecho
// de pedirlo.
//
// OJO — `isDeleteRequestEvent` NO vale para fichajes (exige un tipo de
// periodo) y NO se toca: media app cuenta con que un CLOCK_IN con el flag
// siga siendo un fichaje normal. Por eso hay un predicado aparte.
export const CLOCK_EVENT_TYPES = [EVENT_TYPES.CLOCK_IN, EVENT_TYPES.CLOCK_OUT];

export const isClockEventType = (eventType) => CLOCK_EVENT_TYPES.includes(Number(eventType));

// ¿Es este fichaje (entrada/salida) una solicitud de eliminación pendiente?
export const isClockDeleteRequest = (e) =>
  !!e && isClockEventType(e.eventType) && hasToken(e.geoInfo, 'reqdelete', '1');

// Cualquier solicitud de eliminación: periodo (dos extremos) o fichaje
// suelto. Es lo que mira la pantalla de Aprobaciones para pintarla y para
// enrutarla al handler de eliminación en lugar del de aprobación normal.
export const isAnyDeleteRequest = (e) => isDeleteRequestEvent(e) || isClockDeleteRequest(e);

// Busca el otro extremo del periodo de `event` entre `events` (mismo
// trabajador), con el MISMO emparejado que pairEventsIntoPeriods.
export const findPeriodPartner = (events, event) => {
  const pair = event ? periodPairFor(event.eventType) : null;
  if (!pair) return null;
  const sameWorker = (Array.isArray(events) ? events : [])
    .filter((e) => e && Number(e.workerId) === Number(event.workerId));
  for (const p of pairEventsIntoPeriods(sameWorker, pair[0], pair[1])) {
    if (!p.endEvent) continue;
    if (p.startId === event.id) return p.endEvent;
    if (p.endId === event.id) return p.startEvent;
  }
  return null;
};

// ── Filtro local por rango de fechas ──────────────────────────────────────
// GET /api/event/worker/:id/:start/:end era EXCLUSIVO en el día final (cortaba
// a las 00:00Z) y las pantallas pedían `end + 1 día` para compensarlo. El
// backend ya es inclusivo, así que esa petición devuelve un día de más.
// Seguimos pidiendo `end + 1` (correcto con backend viejo y nuevo) y
// recortamos aquí al rango INCLUSIVO [startIso, endIso] por DÍA en la zona del
// trabajador (`timeZone`, por defecto Madrid). Como el día de Madrid empieza
// a las 22:00/23:00 UTC del anterior, conviene pedir también `start - 1`
// (addDaysToDayKey) para no perder los fichajes de madrugada.
export const filterEventsToLocalRange = (events, startIso, endIso, timeZone = DEFAULT_TIME_ZONE) => {
  const list = Array.isArray(events) ? events : [];
  return list.filter((e) => {
    if (!e || !e.timestamp) return false;
    const iso = dayKeyInZone(e.timestamp, timeZone);
    if (!iso) return false;
    if (startIso && iso < startIso) return false;
    if (endIso && iso > endIso) return false;
    return true;
  });
};
